import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, GeoPoint } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import ngeohash from 'ngeohash';
import AdminLayout from './AdminLayout';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiZm9keWZlZSIsImEiOiJjbW9oajFwM2wwMDBnMnJxNDhuMTJzMGJmIn0.6uGoChaqV6fu36VllxEN5g';

const searchMapboxAddress = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&types=address&limit=5`
    );
    if (!response.ok) return [];
    const data = await response.json();
    return (data.features || []).map(f => {
      const context = f.context || [];
      let city, state, zip, country;
      for (const ctx of context) {
        if (ctx.id?.startsWith('place')) city = ctx.text;
        else if (ctx.id?.startsWith('region')) state = ctx.text;
        else if (ctx.id?.startsWith('postcode')) zip = ctx.text;
        else if (ctx.id?.startsWith('country')) country = ctx.text;
      }
      const streetNum = f.address || '';
      const streetName = f.text || '';
      const streetAddress = streetNum ? `${streetNum} ${streetName}` : streetName;
      return {
        id: f.id,
        streetAddress: streetAddress.trim(),
        city,
        state,
        zip,
        country,
        latitude: f.geometry?.coordinates?.[1],
        longitude: f.geometry?.coordinates?.[0],
      };
    });
  } catch (e) {
    return [];
  }
};

const CustomersPage = () => {
  const [user, setUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState({}); // customerId -> addresses[]

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadCustomers(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchQuery, customers]);

  const loadCustomers = async (uid) => {
    try {
      const db = getFirestore();
      const customersSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'customers'), orderBy('name'))
      );
      const custs = customersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCustomers(custs);
      setFilteredCustomers(custs);

      // Load addresses for each customer
      const addrMap = {};
      for (const cust of custs) {
        const addrSnapshot = await getDocs(
          collection(db, 'users', uid, 'customers', cust.id, 'addresses')
        );
        addrMap[cust.id] = addrSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      setCustomerAddresses(addrMap);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchQuery) {
      setFilteredCustomers(customers);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredCustomers(customers.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query) ||
        c.phone?.includes(query)
      ));
    }
  };

  const handleDeleteCustomer = async (customer) => {
    if (!user) return;
    const result = await Swal.fire({
      title: 'Delete Customer?',
      text: `Are you sure you want to delete "${customer.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const db = getFirestore();

        // Delete Stripe customer if linked
        if (customer.stripeCustomerId) {
          try {
            const functions = getFunctions();
            const deleteStripeCustomerFn = httpsCallable(functions, 'deleteStripeCustomer');
            await deleteStripeCustomerFn({ stripeCustomerId: customer.stripeCustomerId });
          } catch (stripeError) {
            console.error('Stripe customer delete error:', stripeError);
          }
        }

        // Delete nested addresses first
        const addrSnapshot = await getDocs(
          collection(db, 'users', user.uid, 'customers', customer.id, 'addresses')
        );
        const deletePromises = addrSnapshot.docs.map(d =>
          deleteDoc(doc(db, 'users', user.uid, 'customers', customer.id, 'addresses', d.id))
        );
        await Promise.all(deletePromises);
        // Delete customer
        await deleteDoc(doc(db, 'users', user.uid, 'customers', customer.id));
        await loadCustomers(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Customer has been deleted', 'success');
      } catch (error) {
        console.error('Error deleting customer:', error);
        Swal.fire('Error', 'Failed to delete customer', 'error');
      }
    }
  };

  const handleSaveCustomer = async (customerData) => {
    if (!user) return;
    try {
      const db = getFirestore();
      const customerRef = editingCustomer
        ? doc(db, 'users', user.uid, 'customers', editingCustomer.id)
        : doc(collection(db, 'users', user.uid, 'customers'));

      const position = (customerData.latitude != null && customerData.longitude != null)
        ? { geohash: ngeohash.encode(customerData.latitude, customerData.longitude, 9), geopoint: new GeoPoint(customerData.latitude, customerData.longitude) }
        : null;
      const data = {
        name: customerData.name,
        email: customerData.email || null,
        phone: customerData.phone || null,
        address: customerData.address || null,
        city: customerData.city || null,
        state: customerData.state || null,
        zipCode: customerData.zipCode || null,
        country: customerData.country || null,
        latitude: customerData.latitude || null,
        longitude: customerData.longitude || null,
        position: position,
        updatedAt: new Date(),
      };

      if (!editingCustomer) {
        data.createdAt = new Date();
      }

      await setDoc(customerRef, data, { merge: true });

      // Save new addresses to subcollection
      if (customerData.newAddresses?.length > 0) {
        for (const addr of customerData.newAddresses) {
          await setDoc(doc(collection(db, 'users', user.uid, 'customers', customerRef.id, 'addresses')), {
            ...addr,
            createdAt: new Date(),
          });
        }
      }

      // Delete removed addresses
      if (customerData.deletedAddressIds?.length > 0) {
        for (const addrId of customerData.deletedAddressIds) {
          await deleteDoc(doc(db, 'users', user.uid, 'customers', customerRef.id, 'addresses', addrId));
        }
      }

      // Sync with Stripe
      if (editingCustomer?.stripeCustomerId) {
        try {
          const functions = getFunctions();
          const updateStripeCustomerFn = httpsCallable(functions, 'updateStripeCustomer');
          await updateStripeCustomerFn({
            stripeCustomerId: editingCustomer.stripeCustomerId,
            name: customerData.name,
            email: customerData.email || null,
            phone: customerData.phone || null,
            address: customerData.address || null,
            city: customerData.city || null,
            state: customerData.state || null,
            zipCode: customerData.zipCode || null,
            country: customerData.country || null,
          });
        } catch (stripeError) {
          console.error('Stripe customer update error:', stripeError);
          Swal.fire({
            icon: 'warning',
            title: 'Stripe Sync Failed',
            text: 'Customer was updated locally but could not be synced to Stripe.',
          });
        }
      } else if (!editingCustomer) {
        try {
          const functions = getFunctions();
          const createStripeCustomerFn = httpsCallable(functions, 'createStripeCustomer');
          await createStripeCustomerFn({
            customerId: customerRef.id,
            name: customerData.name,
            email: customerData.email || null,
            phone: customerData.phone || null,
            address: customerData.address || null,
            city: customerData.city || null,
            state: customerData.state || null,
            zipCode: customerData.zipCode || null,
            country: customerData.country || null,
          });
        } catch (stripeError) {
          console.error('Stripe customer creation error:', stripeError);
          Swal.fire({
            icon: 'warning',
            title: 'Stripe Sync Failed',
            text: 'Customer was created locally but could not be synced to Stripe.',
          });
        }
      }

      setShowCustomerModal(false);
      setEditingCustomer(null);
      await loadCustomers(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', editingCustomer ? 'Customer updated' : 'Customer created', 'success');
    } catch (error) {
      console.error('Error saving customer:', error);
      Swal.fire('Error', 'Failed to save customer', 'error');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <AdminLayout title="Customers">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Customers">
      <div className="customers-page">
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <div className="input-group-append">
                  <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6 text-right">
            <button
              className="btn btn-primary"
              style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
              onClick={() => { setEditingCustomer(null); setShowCustomerModal(true); }}
            >
              <i className="fas fa-plus mr-2"></i>Add Customer
            </button>
          </div>
        </div>

        {filteredCustomers.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-users fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">{searchQuery ? 'No customers found' : 'No customers yet'}</h4>
            {!searchQuery && <p className="text-muted">Add your first customer to get started</p>}
          </div>
        ) : (
          <div className="row">
            {filteredCustomers.map((customer) => {
              const addresses = customerAddresses[customer.id] || [];
              return (
                <div key={customer.id} className="col-md-6 col-lg-4 mb-4">
                  <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className="d-flex align-items-center justify-content-center mr-3"
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            background: '#ff6b35',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                          }}
                        >
                          {getInitials(customer.name)}
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="card-title font-weight-bold m-0" style={{ fontSize: '1rem' }}>{customer.name}</h5>
                          {customer.email && (
                            <small className="text-muted"><i className="fas fa-envelope mr-1"></i>{customer.email}</small>
                          )}
                        </div>
                      </div>

                      {customer.phone && (
                        <p className="mb-1" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-phone mr-2 text-muted"></i>{customer.phone}
                        </p>
                      )}
                      {customer.address && (
                        <p className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>
                          <i className="fas fa-map-marker-alt mr-2"></i>
                          {customer.address}{customer.city ? `, ${customer.city}` : ''}
                        </p>
                      )}
                      {addresses.length > 0 && (
                        <p className="mb-0 mt-2" style={{ fontSize: '0.8rem' }}>
                          <span className="badge badge-light">
                            <i className="fas fa-map-pin mr-1"></i>{addresses.length} additional address{addresses.length > 1 ? 'es' : ''}
                          </span>
                        </p>
                      )}

                      <div className="d-flex justify-content-end mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                        <button
                          className="btn btn-sm btn-outline-primary mr-2"
                          onClick={() => { setEditingCustomer(customer); setShowCustomerModal(true); }}
                        >
                          <i className="fas fa-edit mr-1"></i>Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteCustomer(customer)}
                        >
                          <i className="fas fa-trash mr-1"></i>Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showCustomerModal && (
          <CustomerFormModal
            customer={editingCustomer}
            existingAddresses={customerAddresses[editingCustomer?.id] || []}
            onSave={handleSaveCustomer}
            onClose={() => { setShowCustomerModal(false); setEditingCustomer(null); }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

const CustomerFormModal = ({ customer, existingAddresses, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    address: customer?.address || '',
    city: customer?.city || '',
    state: customer?.state || '',
    zipCode: customer?.zipCode || '',
    country: customer?.country || '',
    latitude: customer?.latitude || null,
    longitude: customer?.longitude || null,
  });
  const [otherAddresses, setOtherAddresses] = useState(existingAddresses);
  const [deletedAddressIds, setDeletedAddressIds] = useState([]);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Customer name is required';
    // Optional but must be valid when provided (receipts + loyalty comms).
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) errs.email = 'Enter a valid email address';
    if (formData.phone && formData.phone.replace(/[^\d]/g, '').length < 10) errs.phone = 'Enter a valid phone (10+ digits)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        newAddresses: otherAddresses.filter(a => a.isNew),
        deletedAddressIds,
      });
    }
  };

  const handleDeleteAddress = (addr) => {
    if (addr.isNew) {
      setOtherAddresses(prev => prev.filter(a => a !== addr));
    } else {
      setOtherAddresses(prev => prev.filter(a => a.id !== addr.id));
      setDeletedAddressIds(prev => [...prev, addr.id]);
    }
  };

  const handleAddressAdd = (newAddr, setAsDefault) => {
    if (setAsDefault) {
      setFormData(prev => ({
        ...prev,
        address: newAddr.address,
        city: newAddr.city,
        state: newAddr.state,
        zipCode: newAddr.zipCode,
        country: newAddr.country,
        latitude: newAddr.latitude,
        longitude: newAddr.longitude,
      }));
    }
    setOtherAddresses(prev => [...prev, { ...newAddr, isNew: true, id: `new-${Date.now()}` }]);
    setShowAddAddressModal(false);
  };

  return (
    <>
      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title font-weight-bold">{customer ? 'Edit Customer' : 'Add Customer'}</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Customer Name *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                {/* Default Address */}
                <div className="mt-4 mb-3">
                  <h6 className="font-weight-bold">Default Address</h6>
                </div>

                <MapboxAddressField
                  label="Address"
                  value={formData.address}
                  onChange={(addr) => setFormData(prev => ({
                    ...prev,
                    address: addr.streetAddress,
                    city: addr.city || '',
                    state: addr.state || '',
                    zipCode: addr.zip || '',
                    country: addr.country || '',
                    latitude: addr.latitude,
                    longitude: addr.longitude,
                  }))}
                />

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Zip Code</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {formData.latitude != null && formData.longitude != null && (
                  <div className="alert alert-light border" style={{ borderRadius: '12px' }}>
                    <i className="fas fa-map-marker-alt text-primary mr-2"></i>
                    <strong>Location saved</strong>
                    <div className="text-muted small">
                      Lat: {formData.latitude?.toFixed(6)}, Lng: {formData.longitude?.toFixed(6)}
                    </div>
                  </div>
                )}

                {/* Other Addresses */}
                <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                  <h6 className="font-weight-bold m-0">Other Customer Addresses</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowAddAddressModal(true)}
                  >
                    <i className="fas fa-plus mr-1"></i>Add Address
                  </button>
                </div>

                {otherAddresses.length === 0 ? (
                  <div className="text-center py-3" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                    <p className="text-muted mb-0">No additional addresses</p>
                  </div>
                ) : (
                  otherAddresses.map((addr, idx) => (
                    <div key={addr.id || idx} className="card mb-2" style={{ borderRadius: '12px' }}>
                      <div className="card-body p-3 d-flex justify-content-between align-items-center">
                        <div>
                          <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                            <i className="fas fa-location-arrow text-primary mr-2"></i>
                            {addr.address}
                          </div>
                          <small className="text-muted">
                            {addr.city}{addr.city && addr.state ? ', ' : ''}{addr.state} {addr.zipCode}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          onClick={() => handleDeleteAddress(addr)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                  {customer ? 'Update' : 'Create'} Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      {showAddAddressModal && (
        <AddAddressModal
          onSave={handleAddressAdd}
          onClose={() => setShowAddAddressModal(false)}
        />
      )}
    </>
  );
};

const MapboxAddressField = ({ label, value, onChange }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        setIsSearching(true);
        const results = await searchMapboxAddress(val);
        setSuggestions(results);
        setIsSearching(false);
      } else {
        setSuggestions([]);
      }
    }, 500);
  };

  const handleSelect = (addr) => {
    setQuery(addr.streetAddress);
    setSuggestions([]);
    onChange(addr);
  };

  return (
    <div className="form-group position-relative">
      <label>{label}</label>
      <div className="input-group">
        <div className="input-group-prepend">
          <span className="input-group-text"><i className="fas fa-search"></i></span>
        </div>
        <input
          type="text"
          className="form-control"
          placeholder="Search for an address..."
          value={query}
          onChange={handleChange}
        />
        {isSearching && (
          <div className="input-group-append">
            <span className="input-group-text"><i className="fas fa-spinner fa-spin"></i></span>
          </div>
        )}
        {query && !isSearching && (
          <div className="input-group-append">
            <button className="btn btn-outline-secondary" onClick={() => { setQuery(''); setSuggestions([]); }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className="list-group position-absolute w-100" style={{ zIndex: 1060, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              className="list-group-item list-group-item-action py-2"
              onClick={() => handleSelect(s)}
            >
              <div className="d-flex align-items-center">
                <i className="fas fa-map-marker-alt text-primary mr-2"></i>
                <div className="text-left">
                  <div className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{s.streetAddress}</div>
                  <small className="text-muted">{s.city}{s.city && s.state ? ', ' : ''}{s.state} {s.zip}</small>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AddAddressModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    latitude: null,
    longitude: null,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.address.trim()) errs.address = 'Address is required';
    if (!formData.city.trim()) errs.city = 'City is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      Swal.fire({
        title: 'Set as Default?',
        text: "Do you want to use this as the customer's default address?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ff6b35',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        reverseButtons: true,
      }).then((result) => {
        onSave(formData, result.isConfirmed);
      });
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">Add New Address</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <MapboxAddressField
                label="Address"
                value={formData.address}
                onChange={(addr) => setFormData({
                  address: addr.streetAddress,
                  city: addr.city || '',
                  state: addr.state || '',
                  zipCode: addr.zip || '',
                  country: addr.country || '',
                  latitude: addr.latitude,
                  longitude: addr.longitude,
                })}
              />
              {errors.address && <small className="text-danger">{errors.address}</small>}

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                    {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {formData.latitude != null && formData.longitude != null && (
                <div className="alert alert-light border mt-2" style={{ borderRadius: '12px' }}>
                  <i className="fas fa-map-marker-alt text-primary mr-2"></i>
                  <strong>Location saved</strong>
                  <div className="text-muted small">
                    Lat: {formData.latitude?.toFixed(6)}, Lng: {formData.longitude?.toFixed(6)}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                Add Address
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;
